'use client';

/**
 * Quick Presets Component
 *
 * Collapsible section with preset image cards for quick prompt selection.
 * Shows categorized presets like Swimsuit, Casual, Sitting, Lying, etc.
 * Some presets may be blurred for premium-only content.
 */
import { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Crown, Lock, Zap } from 'lucide-react';

interface PresetItem {
  id: string;
  label: string;
  prompt: string;
  imageUrl: string;
  isPremium?: boolean;
}

interface QuickPresetsProps {
  selectedPresetId: string | null;
  onSelectPreset: (preset: PresetItem | null) => void;
  isPremiumUser?: boolean;
}

// Preset categories with actual images from public folder
const PRESETS: PresetItem[] = [
  {
    id: 'swimsuit',
    label: 'Swimsuit',
    prompt:
      'wearing a stylish swimsuit, beach setting, sunny day, ocean in background, relaxed pose',
    imageUrl: '/images/media-generation/quick-presets/swimsuit.avif',
    isPremium: false,
  },
  {
    id: 'casual',
    label: 'Casual',
    prompt:
      'casual outfit, relaxed setting, natural lighting, candid pose, everyday look',
    imageUrl: '/images/media-generation/quick-presets/casual.avif',
    isPremium: false,
  },
  {
    id: 'sitting',
    label: 'Sitting',
    prompt:
      'sitting pose, comfortable setting, elegant posture, looking at camera, soft lighting',
    imageUrl: '/images/media-generation/quick-presets/sitting.avif',
    isPremium: false,
  },
  {
    id: 'lying',
    label: 'Lying',
    prompt:
      'lying down pose, relaxed expression, soft bedding or couch, intimate setting, warm lighting',
    imageUrl: '/images/media-generation/quick-presets/lying.avif',
    isPremium: true,
  },
  {
    id: 'lingerie',
    label: 'Lingerie',
    prompt:
      'elegant lingerie, bedroom setting, soft ambient lighting, confident pose, artistic composition',
    imageUrl: '/images/media-generation/quick-presets/lingerie.avif',
    isPremium: true,
  },
  {
    id: 'selfie',
    label: 'Selfie',
    prompt:
      'taking a selfie, looking at camera, playful expression, close-up shot, natural lighting',
    imageUrl: '/images/media-generation/quick-presets/selfie.avif',
    isPremium: false,
  },
  {
    id: 'workout',
    label: 'Workout',
    prompt:
      'athletic wear, gym setting, fit and active pose, energetic mood, dynamic lighting',
    imageUrl: '/images/media-generation/quick-presets/workout.avif',
    isPremium: false,
  },
  {
    id: 'elegant',
    label: 'Elegant',
    prompt:
      'elegant dress, sophisticated setting, graceful pose, glamorous lighting, high fashion vibe',
    imageUrl: '/images/media-generation/quick-presets/elegant.avif',
    isPremium: false,
  },
];

export function QuickPresets({
  selectedPresetId,
  onSelectPreset,
  isPremiumUser = false,
}: QuickPresetsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handlePresetClick = (preset: PresetItem) => {
    if (preset.isPremium && !isPremiumUser) {
      // Could show upgrade modal here
      return;
    }
    // Toggle selection - clicking the same preset again deselects it
    if (selectedPresetId === preset.id) {
      onSelectPreset(null);
    } else {
      onSelectPreset(preset);
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/5 bg-[#1a1a2e]">
      {/* Header - Clickable to expand */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-white/5"
      >
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500">
            <Zap className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold text-green-400">
            Quick Presets
          </span>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </motion.div>
      </button>

      {/* Expandable Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4">
              <p className="mb-4 text-sm text-gray-400">
                Click a preset to quickly apply a prompt template
              </p>

              {/* Presets Grid */}
              <div className="grid grid-cols-2 gap-2.5 min-[480px]:grid-cols-3 sm:grid-cols-4 sm:gap-3">
                {PRESETS.map((preset, index) => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    isSelected={selectedPresetId === preset.id}
                    isLocked={preset.isPremium && !isPremiumUser}
                    onClick={() => handlePresetClick(preset)}
                    delay={index * 0.03}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface PresetCardProps {
  preset: PresetItem;
  isSelected: boolean;
  isLocked: boolean;
  onClick: () => void;
  delay: number;
}

function PresetCard({
  preset,
  isSelected,
  isLocked,
  onClick,
  delay,
}: PresetCardProps) {
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      onClick={onClick}
      disabled={isLocked}
      className={`group relative overflow-hidden rounded-xl border-2 transition-all ${
        isSelected
          ? 'border-eros-pink shadow-[0_0_15px_rgba(236,72,153,0.4)]'
          : isLocked
            ? 'border-white/5 opacity-60'
            : 'border-white/10 hover:border-white/30'
      }`}
    >
      {/* Image */}
      <div className="aspect-[3/4] overflow-hidden">
        <img
          src={preset.imageUrl}
          alt={preset.label}
          className={`h-full w-full object-cover transition-all ${
            isLocked ? 'blur-md' : 'group-hover:scale-105'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      </div>

      {/* Lock overlay for premium */}
      {isLocked && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
          <div className="mb-1 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-orange-500">
            <Crown className="h-4 w-4 text-white" />
          </div>
          <span className="text-[10px] font-medium text-white">Premium</span>
        </div>
      )}

      {/* Selection checkmark */}
      {isSelected && !isLocked && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute top-1.5 left-1.5"
        >
          <div className="bg-eros-pink flex h-5 w-5 items-center justify-center rounded-full">
            <svg
              className="h-3 w-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </motion.div>
      )}

      {/* Label */}
      <div className="absolute right-0 bottom-0 left-0 p-2">
        <p className="truncate text-center text-xs font-medium text-white">
          {preset.label}
        </p>
      </div>
    </motion.button>
  );
}

// Export preset type for use in parent components
export type { PresetItem };
